import { StyleSheet } from 'react-native';
import { Surface, Text, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import type { FundingDisplay } from '../api/apiService';

export interface DriverFundingSummaryProps {
  funding?: FundingDisplay | null;
  relationshipStatus?: string | null;
  driverName?: string | null;
  prominent?: boolean;
  compact?: boolean;
  testID?: string;
}

const getRelationshipFallback = (status?: string | null): string => {
  switch (status) {
    case 'active':
      return 'Active';
    case 'associated':
      return 'Associated';
    case 'pending':
      return 'Pending';
    case 'deactivated':
      return 'Deactivated';
    default:
      return 'Unknown';
  }
};

const getPayerFallback = (payer?: FundingDisplay['payer']): string => {
  switch (payer) {
    case 'self':
      return 'Driver (self-pay)';
    case 'company':
      return 'Company';
    case 'ambiguous':
      return 'Needs review';
    case 'uncovered':
      return 'Uncovered';
    default:
      return 'Unknown';
  }
};

const getCoverageFallback = (coverageStatus?: FundingDisplay['coverage_status']): string => {
  switch (coverageStatus) {
    case 'active':
      return 'Active';
    case 'awaiting':
      return 'Awaiting coverage';
    case 'not_covered':
      return 'No active coverage';
    default:
      return 'Unknown';
  }
};

export interface LicenseIssueCopy {
  issue: NonNullable<FundingDisplay['license_issue']> | null;
  label: string | null;
  action: string | null;
}

/** ALL-126: owner-facing badge and next step when a self-pay licence failed payment. */
export const useLicenseIssueCopy = (
  funding?: FundingDisplay | null,
  relationshipStatus?: string | null,
  driverName?: string | null,
): LicenseIssueCopy => {
  const { t } = useTranslation('manageDrivers');
  const issue = relationshipStatus !== 'deactivated' && funding?.payer === 'self'
    ? funding.license_issue ?? null
    : null;
  if (!issue) {
    return { issue: null, label: null, action: null };
  }
  const name = driverName || t('funding.licenseIssues.thisDriver', 'This driver');
  if (issue === 'canceled_nonpayment') {
    return {
      issue,
      label: t('funding.licenseIssues.canceledNonpayment', 'Cancelled — payment failed'),
      action: t(
        'funding.licenseIssues.canceledNonpaymentAction',
        '{{name}} can no longer log work. Ask them to activate a new license, or send them a new company-paid invitation under Drivers.',
        { name },
      ),
    };
  }
  return {
    issue,
    label: t('funding.licenseIssues.paymentOverdue', 'Payment overdue'),
    action: t(
      'funding.licenseIssues.paymentOverdueAction',
      "{{name}} can't log work until the payment is fixed. Ask them to update their payment method, or send them a new company-paid invitation under Drivers.",
      { name },
    ),
  };
};

export const DriverLicenseIssueNotice = ({
  funding,
  relationshipStatus,
  driverName,
  testID,
}: Pick<DriverFundingSummaryProps, 'funding' | 'relationshipStatus' | 'driverName' | 'testID'>) => {
  const theme = useTheme();
  const { label, action } = useLicenseIssueCopy(funding, relationshipStatus, driverName);
  if (!label) {
    return null;
  }
  return (
    <Surface
      elevation={0}
      testID={testID}
      style={[styles.issue, { backgroundColor: theme.colors.errorContainer }]}
    >
      <Text variant="titleSmall" style={{ color: theme.colors.onErrorContainer, fontWeight: '700' }}>
        {label}
      </Text>
      <Text variant="bodyMedium" style={{ color: theme.colors.onErrorContainer }}>
        {action}
      </Text>
    </Surface>
  );
};

export const DriverFundingSummary = ({
  funding,
  relationshipStatus,
  driverName,
  prominent = false,
  compact = false,
  testID,
}: DriverFundingSummaryProps) => {
  const { t } = useTranslation('manageDrivers');
  const theme = useTheme();

  const relationship = t(
    `funding.relationshipStates.${relationshipStatus || 'unknown'}`,
    getRelationshipFallback(relationshipStatus),
  );
  const payer = t(
    `funding.payers.${funding?.payer || 'unknown'}`,
    getPayerFallback(funding?.payer),
  );
  const coverage = t(
    `funding.coverageStates.${funding?.coverage_status || 'unknown'}`,
    getCoverageFallback(funding?.coverage_status),
  );
  const coverageLabel = funding?.payer === 'self'
    ? t('funding.personalCoverageLabel', 'Personal coverage')
    : funding?.payer === 'company'
      ? t('funding.companyCoverageLabel', 'Company coverage')
      : t('funding.coverageLabel', 'Coverage');
  const seatConsumption = funding?.seat_consumption ?? (funding?.consumes_corporate_seat ? 1 : 0);
  const corporateSeatUse = !funding
    ? t('funding.corporateSeatUse.unknown', 'Unknown')
    : funding.payer === 'ambiguous'
      ? t('funding.corporateSeatUse.ambiguous', '{{count}} (needs review; counted in company use)', {
          count: seatConsumption || 1,
        })
      : funding.payer === 'uncovered'
        ? t('funding.corporateSeatUse.uncovered', '0 (no active coverage)')
        : funding.payer === 'self'
      ? t('funding.corporateSeatUse.self', '0 (personal license; no corporate seat)')
      : funding.consumes_corporate_seat === false
        ? t('funding.corporateSeatUse.none', '0 (no corporate seat)')
        : t('funding.corporateSeatUse.company', '{{count}} corporate seat', {
            count: seatConsumption,
          });

  const { label: licenseIssueLabel, action: licenseIssueAction } = useLicenseIssueCopy(
    funding, relationshipStatus, driverName,
  );

  const compactStatus = relationshipStatus === 'deactivated'
    ? t('funding.compactStatuses.deactivated', 'Deactivated')
    : licenseIssueLabel
      ? licenseIssueLabel
    : funding?.coverage_status === 'active'
      ? t('funding.compactStatuses.active', 'Active')
      : funding?.coverage_status === 'awaiting'
        ? t('funding.compactStatuses.awaiting', 'Awaiting license')
        : relationshipStatus === 'pending'
          ? t('funding.compactStatuses.pending', 'Pending')
          : funding?.coverage_status === 'not_covered'
            ? t('funding.compactStatuses.notCovered', 'No active license')
            : relationshipStatus === 'associated'
              ? t('funding.compactStatuses.associated', 'Associated')
              : t('funding.compactStatuses.unknown', 'Unknown');
  const compactPayer = t(
    `funding.compactPayers.${funding?.payer || 'unknown'}`,
    funding?.payer === 'self'
      ? 'Self-pay'
      : funding?.payer === 'company'
        ? 'Company pays'
        : funding?.payer === 'ambiguous'
          ? 'To confirm'
          : funding?.payer === 'uncovered'
            ? 'No active payer'
            : 'Unknown',
  );

  return (
    <Surface
      testID={testID}
      elevation={prominent ? 2 : 0}
      style={[
        styles.container,
        prominent && {
          backgroundColor: theme.colors.primaryContainer,
          borderColor: theme.colors.primary,
          borderWidth: 1,
        },
      ]}
    >
      {compact ? (
        <>
          <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
            {compactStatus} · {compactPayer}
          </Text>
          {licenseIssueAction && (
            <Text
              variant="bodySmall"
              testID={testID ? `${testID}-license-issue` : undefined}
              style={{ color: theme.colors.error }}
            >
              {licenseIssueAction}
            </Text>
          )}
        </>
      ) : (
        <>
      {prominent && (
        <Text variant="titleMedium" style={{ color: theme.colors.onPrimaryContainer, fontWeight: '700', marginBottom: 8 }}>
          {t('funding.summaryTitle', 'Licensing and funding')}
        </Text>
      )}
      <Text variant={prominent ? 'bodyLarge' : 'bodySmall'}>
        {t('funding.relationshipLabel', 'Relationship')}: {relationship}
      </Text>
      <Text variant={prominent ? 'bodyLarge' : 'bodySmall'}>
        {t('funding.payerLabel', 'Payer')}: {payer}
      </Text>
      <Text variant={prominent ? 'bodyLarge' : 'bodySmall'}>
        {coverageLabel}: {licenseIssueLabel ?? coverage}
      </Text>
      <DriverLicenseIssueNotice
        funding={funding}
        relationshipStatus={relationshipStatus}
        driverName={driverName}
        testID={testID ? `${testID}-license-issue` : undefined}
      />
      <Text variant={prominent ? 'bodyLarge' : 'bodySmall'}>
        {t('funding.corporateSeatUseLabel', 'Corporate-seat use')}: {corporateSeatUse}
      </Text>
        </>
      )}
    </Surface>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingVertical: 4,
    gap: 2,
    backgroundColor: 'transparent',
  },
  issue: {
    marginTop: 8,
    padding: 12,
    borderRadius: 8,
    gap: 4,
  },
});
